import type { OrgClientSchemaType, OrgSchemaType } from '@/types/portal-api.generated'
import { normalizeClientId } from '@/utilities/auth/authHelper'
import {
  canSelectOrgForPrimaryPartner,
  isPartnerInIdentityScopes,
} from '@/utilities/dashboard/orgProgramAuthorization'
import {
  hasClientSuperAdminRoleInScopes,
  type IdentityScopes,
} from '@/utilities/auth/identityScopeRoles'

export type SwitcherProgram = {
  id: string
  name: string
}

export type SwitcherOrganization = {
  /** CMS organization id (grouping key in the UI). */
  id: string
  name: string
  /** Primary partner id used for `org=` URL values. */
  primaryPartnerId: string | null
  /** Whether the user may select org ("All programs") context for this org. */
  canSelectOrg: boolean
  programs: SwitcherProgram[]
}

export type SwitcherSelection =
  | {
      mode: 'org'
      organizationId: string
      primaryPartnerId: string
      organizationName: string
      /** Every program the user may see in this org, used to scope org-wide requests. */
      programIds: string[]
    }
  | {
      mode: 'program'
      organizationId: string
      organizationName: string
      /** Partner id from `/partners`. The switcher shows this value as a program. */
      programId: string
      programName: string
    }

/**
 * Partner id for the current switcher selection.
 * An org selection uses the primary partner id.
 * A program selection uses `programId`, which is a partner id from `/partners`.
 */
export function resolveSwitcherSelectionPartnerId(
  selection: SwitcherSelection | null,
): string | null {
  if (selection?.mode === 'org') return selection.primaryPartnerId
  if (selection?.mode === 'program') return selection.programId
  return null
}

function normalizeProgramId(rawId: string): string {
  return normalizeClientId(rawId.trim())
}

function resolvePrimaryPartnerId(clients: readonly OrgClientSchemaType[]): string | null {
  const primary = clients.find((client) => client.is_primary === true)
  const raw = primary?.id?.trim()
  return raw ? normalizeProgramId(raw) : null
}

/**
 * Build the nested switcher tree from `/v2/auth/organizations`.
 * Never invents orgs/programs — only maps the API allow-list.
 * `canSelectOrg` is gated by role access (org/global admin), not per-client `is_admin`.
 */
export function buildOrgProgramSwitcherTree(
  organizations: ReadonlyArray<OrgSchemaType>,
  {
    canSelectOrg,
    identityScopes,
    allowedPartnerIds,
  }: {
    canSelectOrg: boolean
    identityScopes?: IdentityScopes | null
    /** `/partners` is the capability and program-access allowlist. */
    allowedPartnerIds?: readonly string[]
  },
): SwitcherOrganization[] {
  const tree: SwitcherOrganization[] = []
  const shouldFilterByScopes = identityScopes !== undefined
  const isGlobalAdmin = hasClientSuperAdminRoleInScopes(identityScopes)
  const allowedPartners = allowedPartnerIds
    ? new Set(allowedPartnerIds.map((partnerId) => normalizeProgramId(partnerId)))
    : null

  for (const org of organizations) {
    const orgId = org.id?.trim()
    const orgName = org.name?.trim()
    if (!orgId || !orgName) continue

    const allPrograms: SwitcherProgram[] = []
    const seen = new Set<string>()
    for (const client of org.clients ?? []) {
      const rawId = client.id?.trim()
      if (!rawId) continue
      const id = normalizeProgramId(rawId)
      if (allowedPartners && !allowedPartners.has(id)) continue
      if (seen.has(id)) continue
      seen.add(id)
      allPrograms.push({
        id,
        name: client.name?.trim() || id,
      })
    }

    if (allPrograms.length === 0) continue

    allPrograms.sort((a, b) => a.name.localeCompare(b.name))

    const primaryPartnerId = resolvePrimaryPartnerId(org.clients ?? [])
    const canSelectThisOrg =
      canSelectOrg &&
      primaryPartnerId !== null &&
      (allowedPartners === null || allowedPartners.has(primaryPartnerId)) &&
      (!shouldFilterByScopes || canSelectOrgForPrimaryPartner(identityScopes, primaryPartnerId))
    const programs =
      canSelectThisOrg || !shouldFilterByScopes || isGlobalAdmin
        ? allPrograms
        : allPrograms.filter((program) => isPartnerInIdentityScopes(identityScopes, program.id))

    if (programs.length === 0) continue

    tree.push({
      id: orgId,
      name: orgName,
      primaryPartnerId,
      canSelectOrg: canSelectThisOrg,
      programs,
    })
  }

  return tree.sort((a, b) => a.name.localeCompare(b.name))
}

function matchesQuery(haystack: string, query: string): boolean {
  return haystack.toLowerCase().includes(query)
}

/**
 * Filter orgs/programs by search while preserving nesting.
 * Matches org name, program display name, and partner id.
 */
export function filterOrgProgramSwitcherTree(
  organizations: ReadonlyArray<SwitcherOrganization>,
  searchQuery: string,
): SwitcherOrganization[] {
  const query = searchQuery.trim().toLowerCase()
  if (!query) return [...organizations]

  return organizations
    .map((org) => {
      const orgMatches =
        matchesQuery(org.name, query) ||
        (org.primaryPartnerId != null && matchesQuery(org.primaryPartnerId, query))

      const programs = org.programs.filter(
        (program) =>
          orgMatches || matchesQuery(program.name, query) || matchesQuery(program.id, query),
      )

      if (!orgMatches && programs.length === 0) return null

      return {
        ...org,
        // When the org itself matches, keep all programs for orientation.
        programs: orgMatches ? org.programs : programs,
      }
    })
    .filter((org): org is SwitcherOrganization => org !== null)
}

/** Count programs that match the query within an org (for match badges). */
export function countProgramMatches(org: SwitcherOrganization, searchQuery: string): number {
  const query = searchQuery.trim().toLowerCase()
  if (!query) return 0
  return org.programs.filter(
    (program) => matchesQuery(program.name, query) || matchesQuery(program.id, query),
  ).length
}

export type SwitcherOrgViewModel = SwitcherOrganization & {
  orgMatchesSearch: boolean
  matchCount: number
  /** Programs to render for this org under the current search. */
  visiblePrograms: SwitcherProgram[]
  showAllPrograms: boolean
}

const ALL_PROGRAMS_SEARCH_LABEL = 'all programs'

function matchesAllProgramsSearchLabel(query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase().replace(/\s+/g, ' ')
  return (
    normalizedQuery.length >= 'all'.length && ALL_PROGRAMS_SEARCH_LABEL.startsWith(normalizedQuery)
  )
}

/**
 * Single filtered view-model for desktop + mobile switcher panes.
 * Keeps search / All Programs / match-badge rules in one place.
 */
export function buildOrgProgramSwitcherViewModel(
  organizations: ReadonlyArray<SwitcherOrganization>,
  searchQuery: string,
): SwitcherOrgViewModel[] {
  const query = searchQuery.trim().toLowerCase()
  const allProgramsMatches = matchesAllProgramsSearchLabel(query)
  const filtered = allProgramsMatches
    ? organizations.filter((org) => org.canSelectOrg)
    : filterOrgProgramSwitcherTree(organizations, searchQuery)

  return filtered.map((org) => {
    const orgMatchesSearch =
      !query ||
      matchesQuery(org.name, query) ||
      (org.primaryPartnerId != null && matchesQuery(org.primaryPartnerId, query))

    return {
      ...org,
      orgMatchesSearch,
      matchCount: countProgramMatches(org, searchQuery),
      // filterOrgProgramSwitcherTree already narrows programs when only programs match.
      visiblePrograms: allProgramsMatches ? [] : org.programs,
      // Shown for one-program orgs too: the trigger labels org context "All Programs",
      // so hiding the row would leave no way back to a state the user can already be in.
      showAllPrograms: org.canSelectOrg && (!query || orgMatchesSearch || allProgramsMatches),
    }
  })
}

function findProgramSelection(
  organizations: ReadonlyArray<SwitcherOrganization>,
  partnerId: string,
): SwitcherSelection | null {
  for (const org of organizations) {
    const program = org.programs.find((p) => p.id === partnerId)
    if (program) {
      return {
        mode: 'program',
        organizationId: org.id,
        organizationName: org.name,
        programId: program.id,
        programName: program.name,
      }
    }
  }
  return null
}

function findOrganizationByProgramId(
  organizations: ReadonlyArray<SwitcherOrganization>,
  partnerId: string,
): SwitcherOrganization | null {
  for (const org of organizations) {
    if (org.programs.some((program) => program.id === partnerId)) {
      return org
    }
  }
  return null
}

/**
 * Org context for an explicit `?org=` URL. Program count is deliberately not a
 * condition: a one-program org keeps the same URL meaning as it adds programs,
 * and its org scope is simply a list of one.
 */
function buildOrgSelection(org: SwitcherOrganization): SwitcherSelection | null {
  if (!org.canSelectOrg || !org.primaryPartnerId) return null

  return {
    mode: 'org',
    organizationId: org.id,
    primaryPartnerId: org.primaryPartnerId,
    organizationName: org.name,
    programIds: org.programs.map((program) => program.id),
  }
}

/**
 * Default selection when no `?org=` is present. A one-program org lands on that
 * program, so the majority of orgs keep a single-click dashboard.
 */
function resolveOrgSelection(org: SwitcherOrganization): SwitcherSelection | null {
  if (org.canSelectOrg && org.programs.length === 1) {
    const program = org.programs[0]
    return program
      ? {
          mode: 'program',
          organizationId: org.id,
          organizationName: org.name,
          programId: program.id,
          programName: program.name,
        }
      : null
  }

  return buildOrgSelection(org)
}

function resolveFirstProgramSelection(org: SwitcherOrganization): SwitcherSelection | null {
  const program = org.programs[0]
  return program
    ? {
        mode: 'program',
        organizationId: org.id,
        organizationName: org.name,
        programId: program.id,
        programName: program.name,
      }
    : null
}

function resolveDefaultOrgContextSelection(org: SwitcherOrganization): SwitcherSelection | null {
  return resolveOrgSelection(org) ?? resolveFirstProgramSelection(org)
}

export function resolveOrgContextSelection({
  organizations,
  orgParam,
  selectedClientId,
}: {
  organizations: ReadonlyArray<SwitcherOrganization>
  orgParam: string | null | undefined
  selectedClientId: string | null | undefined
}): SwitcherSelection | null {
  const orgIdFromUrl = orgParam?.trim() ? normalizeProgramId(orgParam.trim()) : null

  if (orgIdFromUrl) {
    const byPrimary = organizations.find((org) => org.primaryPartnerId === orgIdFromUrl)
    return byPrimary ? buildOrgSelection(byPrimary) : null
  }

  const selectedProgramId = selectedClientId?.trim()
    ? normalizeProgramId(selectedClientId.trim())
    : null
  if (selectedProgramId) {
    const selectedOrg = findOrganizationByProgramId(organizations, selectedProgramId)
    const selectedProgram = findProgramSelection(organizations, selectedProgramId)
    if (selectedOrg && selectedProgram) {
      return resolveOrgSelection(selectedOrg) ?? selectedProgram
    }
  }

  return organizations.length > 0 ? resolveDefaultOrgContextSelection(organizations[0]!) : null
}

/**
 * Resolve the primary partner of the organization that contains `partnerId`.
 * Org-wide settings, such as the org dashboard variant, come from this partner.
 */
export function resolveOrgPrimaryPartnerIdForPartner(
  organizations: ReadonlyArray<OrgSchemaType>,
  partnerId: string | null | undefined,
): string | null {
  const normalizedPartnerId = partnerId?.trim() ? normalizeProgramId(partnerId.trim()) : null
  if (!normalizedPartnerId) return null

  const org = organizations.find((candidate) =>
    (candidate.clients ?? []).some(
      (client) => client.id?.trim() && normalizeProgramId(client.id.trim()) === normalizedPartnerId,
    ),
  )
  return org ? resolvePrimaryPartnerId(org.clients ?? []) : null
}

/**
 * Resolve the partner whose flags should bootstrap a bare dashboard. This uses
 * the same sorted organization tree and default-selection rules as routing.
 */
export function resolveOrgProgramBootstrapPartnerId(
  organizations: ReadonlyArray<OrgSchemaType>,
  selectedClientId: string | null | undefined,
  allowedPartnerIds?: readonly string[],
): string | null {
  const tree = buildOrgProgramSwitcherTree(organizations, {
    canSelectOrg: true,
    allowedPartnerIds,
  })
  const selection = resolveOrgContextSelection({
    organizations: tree,
    orgParam: null,
    selectedClientId,
  })

  return resolveSwitcherSelectionPartnerId(selection)
}

/**
 * Resolve the current switcher selection from URL/selection + the orgs tree.
 * When `isOrgContext` is true (from the shared dashboard route state), URL
 * org context wins, followed by the org containing selectedClientId, followed
 * by the first selectable org/program fallback. Elsewhere, program context
 * from partners / selectedClientId wins.
 */
export function resolveSwitcherSelection({
  organizations,
  orgParam,
  partnersParam,
  selectedClientId,
  isOrgContext,
  canSelectOrg,
}: {
  organizations: ReadonlyArray<SwitcherOrganization>
  orgParam: string | null | undefined
  partnersParam: string | null | undefined
  selectedClientId: string | null | undefined
  isOrgContext: boolean
  canSelectOrg: boolean
}): SwitcherSelection | null {
  if (canSelectOrg && isOrgContext) {
    return resolveOrgContextSelection({ organizations, orgParam, selectedClientId })
  }

  const fromUrl = partnersParam?.trim()
  const fromSelection = selectedClientId?.trim()
  const partners = fromUrl || fromSelection ? normalizeProgramId((fromUrl ?? fromSelection)!) : null
  if (!partners) return null
  return findProgramSelection(organizations, partners)
}
